'use client';

import { sendVerificationCode, verifyCode } from '@/lib/mfa-client';
import { useEffect, useRef, useState } from 'react';
import { useCountUp } from 'react-countup';

// --- SVG Star Components ---
const StarIcon = ({ fill = 'currentColor', className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill={fill}
    className={className}
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z"
      clipRule="evenodd"
    />
  </svg>
);

const HalfStar = ({ className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    className={className}
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="halfGradient">
        <stop offset="50%" stopColor="#18181b" /> {/* Zinc-900 */}
        <stop offset="50%" stopColor="#d1d5db" /> {/* Gray-300 */}
      </linearGradient>
    </defs>
    <path
      fillRule="evenodd"
      d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z"
      clipRule="evenodd"
      fill="url(#halfGradient)"
    />
  </svg>
);

// --- Resend/Refresh Icon Component ---
const RefreshIcon = ({ className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
    />
  </svg>
);

// --- Loader Icon ---
const LoaderIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={`animate-spin ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

// --- End SVG Components ---

interface ValuationFormProps {
  dictionary: any;
  phoneNumber: string;
  priceRange?: {
    min: number;
    max: number;
  };
  isVerified: boolean;
  setIsVerified: (verified: boolean) => void;
  onGoBack: () => void;
  onVerificationSuccess: () => void;
  onDetailsResponse: (response: 'result') => void;
  onEmailSubmit: (email: string) => void;
  initialStarRating?: number;
  isSubmitted: boolean;
  enableEmailCollection?: boolean;
}

export default function ValuationForm({ dictionary, priceRange, phoneNumber, isVerified, setIsVerified, onGoBack, onVerificationSuccess, onDetailsResponse, onEmailSubmit, initialStarRating, isSubmitted, enableEmailCollection }: ValuationFormProps) {
  const [animationFinished, setAnimationFinished] = useState(false);

  // Refs for the HTML elements where numbers will be displayed
  const minValRef = useRef<HTMLSpanElement>(null);
  const maxValRef = useRef<HTMLSpanElement>(null);

  // --- Verification State (from stashed version) ---
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);

  // --- Timer State ---
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // --- End Timer State ---

  // --- Email Form State ---
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>(isSubmitted ? 'success' : 'idle');
  const [showEmailForm, setShowEmailForm] = useState(false);
  // --- End Email Form State ---

  // Use the hook, passing the ref and configuration
  const { start: startMinCountUp } = useCountUp({
    ref: minValRef, // Target the span element
    start: 0,
    end: priceRange?.min ?? 0, // Use default 0 if priceRange is invalid
    duration: 3,
    separator: '.',
    decimal: ',',
    decimals: 0,
    startOnMount: false, // Don't start automatically
    onEnd: () => setAnimationFinished(true) // Trigger pulse on end
  });

  const { start: startMaxCountUp } = useCountUp({
    ref: maxValRef, // Target the span element
    start: 0,
    end: priceRange?.max ?? 0, // Use default 0 if priceRange is invalid
    duration: 3,
    separator: '.',
    decimal: ',',
    decimals: 0,
    startOnMount: false, // Don't start automatically
    onEnd: () => setAnimationFinished(true) // Trigger pulse on end
  });

  // --- Timer Effect ---
  useEffect(() => {
    if (isCodeSent && !canResend) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            if (timerRef.current) {
              clearInterval(timerRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isCodeSent, canResend]);

  // --- Effect to start animation after a delay - ONLY IF VERIFIED ---
  useEffect(() => {
    if (isVerified) { // Only run if verified
      const timer = setTimeout(() => {
        // Call the start functions returned by the hooks
        startMinCountUp();
        startMaxCountUp();
      }, 500); // 500ms delay

      // Cleanup timeout if component unmounts before delay finishes
      return () => clearTimeout(timer);
    }
  }, [isVerified, startMinCountUp, startMaxCountUp]); // Add isVerified dependency

  // --- NEW Effect: Only show details section after animation finishes ---
  useEffect(() => {
    if (animationFinished) {
      // Show details section after a delay
      const timer = setTimeout(() => {
        setShowEmailForm(true);
      }, 500); // 500ms delay after animation ends
      return () => clearTimeout(timer); // Cleanup timer
    }
  }, [animationFinished]); // Dependency on animationFinished state

  // --- Verification Handlers (from stashed version) ---
  const handleSendCode = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await sendVerificationCode(phoneNumber);

      if (!response.success) {
        throw new Error(
          response.error || dictionary?.sellingForm?.verificationFailedToSend || 'Failed to send verification code'
        );
      }

      setIsCodeSent(true);
      // Reset timer state for resend
      setTimeLeft(60);
      setCanResend(false);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setError(dictionary?.sellingForm?.verificationCodeRequired || 'Verification code is required');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const response = await verifyCode(phoneNumber, verificationCode);

      if (!response.success) {
        throw new Error(
          response.error || dictionary?.sellingForm?.verificationInvalidCode || 'Invalid verification code'
        );
      }

      setIsVerified(true);
      onVerificationSuccess();
      onDetailsResponse('result');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsVerifying(false);
    }
  };

  // --- Resend Code Handler ---
  const handleResendCode = async () => {
    await handleSendCode(); // Reuse the existing send code logic
  };
  // --- End Verification Handlers ---

  // --- Email Submit Handler ---
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Call the parent handler to trigger background tasks
    onEmailSubmit(email);

    // After a short delay to feel responsive, update the UI to show success
    setTimeout(() => {
      setSubmitStatus('success');
      setIsSubmitting(false);
    }, 500);
  };
  // --- End Email Submit Handler ---

  if (!priceRange || typeof priceRange.min !== 'number' || typeof priceRange.max !== 'number') {
    return (
      <div className="space-y-2">
        <div>
          <label htmlFor="valuation" className="block text-base text-zinc-800">
            {dictionary?.sellingForm?.valuationDisplayDescription || 'Estimating the value of your property...'}
          </label>
          <div className="mt-2 p-3 bg-zinc-100 rounded-lg min-h-[44px]">
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isVerified ? (
        // --- Verification UI ---
        <div className="space-y-4">
          <div>
            <label htmlFor="verification" className="block text-base font-medium text-zinc-900">
              {dictionary?.sellingForm?.verificationTitle || 'Phone Verification'}
            </label>
            <p className="mt-1 text-base text-zinc-700">
              {dictionary?.sellingForm?.verificationDescription || 'We need to verify your phone number before showing the valuation.'}
            </p>
          </div>

          {!isCodeSent ? (
            // --- Stack buttons vertically --- 
            <div className="flex flex-col gap-2 items-start">
              <button
                onClick={handleSendCode}
                disabled={isLoading}
                // Adjusted classes for vertical stacking
                className={`w-fit bg-zinc-900 text-white py-2 px-6 rounded-lg transition-colors text-base ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-zinc-800'}`}
              >
                {isLoading
                  ? (dictionary?.sellingForm?.sendingCodeButton || 'Sending...')
                  : (dictionary?.sellingForm?.sendCodeButton || 'Send Verification Code')}
              </button>
              <button
                type="button"
                onClick={onGoBack}
                disabled={isLoading}
                // Adjusted classes for vertical stacking
                className={`w-fit text-zinc-600 py-1 ml-1 text-xs ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:text-zinc-900'}`}
              >
                {/* --- Render dictionary string as HTML --- */}
                <span dangerouslySetInnerHTML={{ __html: dictionary?.sellingForm?.changePhoneButton || 'Change Number' }} />
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder={dictionary?.sellingForm?.verificationCodePlaceholder || 'Enter verification code'}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 bg-zinc-50 placeholder:text-zinc-500 text-base"
                />
              </div>

              {/* Timer and Resend Section */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {!canResend ? (
                    <p className="text-xs text-zinc-600">
                      {(dictionary?.sellingForm?.resendCodeTimer || `New code in ${timeLeft}s`).replace('{timeLeft}', timeLeft.toString())}
                    </p>
                  ) : (
                    <button
                      onClick={handleResendCode}
                      disabled={isLoading}
                      className={`flex items-center space-x-1 text-xs text-zinc-600 underline transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:text-zinc-900'}`}
                    >
                      <RefreshIcon className="w-3 h-3" />
                      <span>{dictionary?.sellingForm?.resendCodeButton || 'Resend code'}</span>
                    </button>
                  )}
                </div>
              </div>

              <button
                onClick={handleVerifyCode}
                disabled={isVerifying}
                className={`w-fit bg-zinc-900 text-white py-2 px-6 rounded-lg transition-colors mt-4 ${isVerifying ? 'opacity-50 cursor-not-allowed' : 'hover:bg-zinc-800'}`}
              >
                {isVerifying
                  ? (dictionary?.sellingForm?.verifyingCodeButton || 'Verifying...')
                  : (dictionary?.sellingForm?.verifyCodeButton || 'Verify Code')}
              </button>
            </div>
          )}

          {error && (
            <div className="text-red-600 text-sm mt-2">
              {error}
            </div>
          )}
        </div>
        // --- End Verification UI ---
      ) : (
        // --- Existing Valuation Display (from current version) ---
        <div className="space-y-2">
          <div>
            <label htmlFor="valuation" className="block text-base text-zinc-800">
              {dictionary?.sellingForm?.valuationDisplayDescription || 'Thank you! Based on all the information you\'ve given me, the estimated value of your property is:'}
            </label>
            <div className="mt-3 px-3 py-4 bg-white rounded-lg shadow-sm border border-gray-200 min-h-[44px]">
              {/* Render spans and attach refs. The hook will populate them. */}
              <p className={`text-2xl font-semibold text-zinc-900 text-center ${animationFinished ? 'animate-pulse-reveal' : ''}`}>
                €<span ref={minValRef}>0</span> - €<span ref={maxValRef}>0</span>
              </p>
              {/* Add bordered star container below the price range, visible only after animation */}
              {animationFinished && initialStarRating && initialStarRating > 0 && ( // Conditionally render only when animation done and stars calculated
                <div className="mt-3 flex items-center justify-center space-x-2"> {/* Container for label and stars */}
                  {/* Label */}
                  <p className="text-sm text-zinc-700 mt-0.5 md:test-xs md:mt-1">
                    {dictionary?.sellingForm?.comparablesAccuracyLabel || 'Accuracy'}
                  </p>
                  {/* Star container */}
                  <div className="flex items-center space-x-0.5"> {/* Adjusted spacing for SVGs */}
                    {[1, 2, 3, 4, 5].map((index) => {
                      if (index <= initialStarRating) {
                        // Full yellow star
                        return <StarIcon key={index} className="w-4 h-4 text-zinc-900" />;
                      } else if (index === Math.ceil(initialStarRating) && initialStarRating % 1 !== 0) {
                        // Half star
                        return <HalfStar key={index} className="w-4 h-4" />;
                      } else {
                        // Empty grey star
                        return <StarIcon key={index} className="w-4 h-4 text-gray-300" />;
                      }
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        // --- End Existing Valuation Display ---
      )}

      {/* --- START Email Form Section --- */}
      {showEmailForm && enableEmailCollection && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          {submitStatus === 'success' ? (
            <p className="text-base text-green-700 bg-green-50 p-3 rounded-md font-medium">
              {dictionary?.sellingForm?.emailSuccessResponse || 'Thank you! We will send you the detailed valuation shortly.'}
            </p>
          ) : (
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <label htmlFor="email-input" className="block text-base text-zinc-800">
                {dictionary?.sellingForm?.emailRequestPrompt || 'Drop your email below if you want to receive a detailed valuation of your property.'}
              </label>
              <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
                <input
                  id="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={dictionary?.sellingForm?.emailPlaceholder || 'your@email.be'}
                  required
                  className="flex-grow p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 bg-zinc-50 placeholder:text-zinc-500 text-sm w-full"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-fit sm:w-auto bg-zinc-900 text-white py-2 px-6 rounded-lg transition-colors hover:bg-zinc-800 text-sm flex items-center justify-center ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? (
                    <LoaderIcon className="h-5 w-5" />
                  ) : (
                    dictionary?.sellingForm?.sendButton || 'Send'
                  )}
                </button>
              </div>
              {submitStatus === 'error' && (
                <p className="text-red-600 text-sm">
                  {dictionary?.sellingForm?.emailErrorResponse || 'Something went wrong. Please try again.'}
                </p>
              )}
            </form>
          )}
        </div>
      )}
      {/* --- END Email Form Section --- */}
    </div>
  );
} 